import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, ChevronDownIcon, ClockIcon, MessageCircleIcon } from 'lucide-react';
import { services, WHATSAPP_URL, type Service } from '../data/site';
import { SparkleIcon, BrushIcon, ShieldNailIcon, StarArtIcon } from './icons/NailIcons';
const iconFor: Record<Service['icon'], React.ComponentType<{
  className?: string;
}>> = {
  sparkle: SparkleIcon,
  brush: BrushIcon,
  shield: ShieldNailIcon,
  star: StarArtIcon
};
const ease = [0.23, 1, 0.32, 1] as const;
function ServiceDetail({
  service
}: {
  service: Service;
}) {
  return <div data-id="element-242" data-mp="u6bcinmp4y8m">
      <div className="mt-5 grid gap-7 rounded-3xl border border-blush-200 bg-blush-50 p-6 sm:p-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-10" data-id="element-243" data-mp="u3yuybkuopw3">
        <img src={service.image} alt={service.imageAlt} loading="lazy" className="aspect-[4/5] w-full rounded-2xl object-cover shadow-soft sm:aspect-[16/10] lg:aspect-[4/5]" data-id="element-244" data-mp="l5wp9iq6992h" />

        <div data-id="element-245" data-mp="36bo9kwibmac9">
          <p className="text-sm font-semibold text-rosegold-dark" data-id="element-246" data-mp="36awle6r1zew0">Así queda el trabajo</p>
          <h4 className="mt-2 font-serif text-3xl font-semibold leading-tight text-ink" data-id="element-247" data-mp="3b3zi2nr4n4gf">
            {service.title}
          </h4>

          <div className="mt-4 space-y-4 text-[0.98rem] leading-relaxed text-ink-soft" data-id="element-248" data-mp="2x9ygzncpd5cw">
            {service.longDescription.map(paragraph => <p key={paragraph.slice(0, 24)} data-id="element-249" data-mp="3q1i6tq3t1hnv">{paragraph}</p>)}
          </div>

          <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-ink" data-id="element-250" data-mp="3u2bx2q4pz8jx">
            <ClockIcon className="h-4 w-4 text-fucsia-deep" aria-hidden="true" data-id="element-251" data-mp="3g1rbjm7f6ymq" />
            {service.duration}
          </p>

          <div className="mt-6 border-t border-blush-300/70 pt-5" data-id="element-252" data-mp="3l3gnr56dpice">
            <p className="text-sm font-semibold uppercase tracking-wider text-ink-soft" data-id="element-253" data-mp="24mci2sef6owd">
              Qué incluye
            </p>
            <ul className="mt-3 grid gap-2.5 sm:grid-cols-2" data-id="element-254" data-mp="29k49llb5k901">
              {service.includes.map(item => <li key={item} className="flex items-start gap-2.5 text-[0.95rem] text-ink" data-id="element-255" data-mp="1lihayxcy8t70">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-fucsia-deep" aria-hidden="true" data-id="element-256" data-mp="1lkv6t8fge9fm" />
                  {item}
                </li>)}
            </ul>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-4" data-id="element-257" data-mp="16ue5dp2tjjiq">
            <a href="#reservar" className="group inline-flex items-center gap-2 rounded-full bg-fucsia-deep px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color] duration-150 ease-smooth hover:-translate-y-0.5 hover:bg-fucsia-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fucsia-deep" data-id="element-258" data-mp="11u07gzuxlpyf">
              Reservar {service.title}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-smooth group-hover:translate-x-1" aria-hidden="true" data-id="element-259" data-mp="2n8mcqcf0pm9s" />
            </a>
            <p className="font-serif text-2xl font-semibold text-fucsia-ink" data-id="element-260" data-mp="2zodr96mrnlbp">{service.price}</p>
          </div>
        </div>
      </div>
    </div>;
}
export function Services() {
  const [openId, setOpenId] = useState<string | null>(null);
  return <section id="servicios" className="w-full bg-white py-20 sm:py-28" data-id="element-261" data-mp="2btb2eynyphv8">
      <div className="mx-auto w-full max-w-7xl px-5 sm:px-8" data-id="element-262" data-mp="2grktoy6kfybk">
        <div className="max-w-2xl" data-id="element-263" data-mp="2grupx3i7f1pp">
          <p className="text-sm font-semibold text-rosegold-dark" data-id="element-264" data-mp="2lqxihogzzfgs">Servicios y precios</p>
          <h2 className="mt-3 font-serif text-4xl font-semibold leading-tight text-ink sm:text-5xl" data-id="element-265" data-mp="2psapi1vttopa">
            Elegí tu técnica
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-ink-soft" data-id="element-266" data-mp="zezc2t5llj9">
            Todos los servicios incluyen prolijado de cutícula, limado y esmaltado. Precios
            transparentes, sin sorpresas al final del turno.
          </p>
        </div>

        <ul className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4" data-id="element-267" data-mp="3mpl4ao5o68y">
          {services.map(service => {
          const Icon = iconFor[service.icon];
          const isOpen = openId === service.id;
          return <React.Fragment key={service.id} data-id="element-268" data-mp="8ixwdcz8ffau">
                <li className={`group flex h-full flex-col rounded-3xl border p-7 transition-[transform,box-shadow,border-color] duration-200 ease-smooth hover:-translate-y-1.5 hover:shadow-lift ${isOpen ? 'border-fucsia-deep/50 bg-blush-100 shadow-lift' : service.featured ? 'border-fucsia-deep/30 bg-blush-100' : 'border-blush-200 bg-blush-50 hover:border-blush-300'}`} data-id="element-269" data-mp="8mbzwojmtujv">
                  <div className="flex items-start justify-between gap-3" data-id="element-270" data-mp="119g10ifltoqd">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-fucsia-deep shadow-soft" data-id="element-271" data-mp="1jx6y6wo4k5wm">
                      <Icon className="h-7 w-7" data-id="element-272" data-mp="1ouqoww20eb5g" />
                    </span>
                    {service.featured && <span className="rounded-full bg-fucsia-deep px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-wider text-white" data-id="element-273" data-mp="1avzepkq9p9ml">
                        Más pedido
                      </span>}
                  </div>

                  <h3 className="mt-6 font-serif text-2xl font-semibold leading-snug text-ink" data-id="element-274" data-mp="15xpcwra1fas0">
                    {service.title}
                  </h3>
                  <p className="mt-3 text-[0.95rem] leading-relaxed text-ink-soft" data-id="element-275" data-mp="s3ofq1yqhvtr">
                    {service.description}
                  </p>

                  <div className="mt-auto pt-7" data-id="element-276" data-mp="i7bmt6b0jm02">
                    <div className="border-t border-blush-300/70 pt-5" data-id="element-277" data-mp="i9ob017d6slk">
                      <p className="font-serif text-3xl font-semibold text-fucsia-ink" data-id="element-278" data-mp="n650ozu7pf0r">
                        {service.price}
                      </p>
                      <p className="mt-1 text-sm text-ink-soft" data-id="element-279" data-mp="2k5dnwnha7rbs">{service.note}</p>
                      <button type="button" onClick={() => setOpenId(isOpen ? null : service.id)} aria-expanded={isOpen} aria-controls={`detalle-${service.id}`} className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-fucsia-deep/30 bg-white px-4 py-2.5 text-sm font-semibold text-fucsia-ink transition-colors duration-150 ease-smooth hover:bg-fucsia-deep hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fucsia-deep" data-id="element-280" data-mp="2k3astp19v807">
                        {isOpen ? 'Leer menos' : 'Leer más'}
                        <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-smooth ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-281" data-mp="1ifrwa2ymgich" />
                      </button>
                    </div>
                  </div>
                </li>

                <AnimatePresence initial={false} data-id="element-282" data-mp="1n0r24b5tgghi">
                  {isOpen && <motion.li key={`detalle-${service.id}`} id={`detalle-${service.id}`} className="col-span-full" initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.28,
                ease
              }} style={{
                overflow: 'hidden'
              }} data-id="element-283" data-mp="1n2u3frcf5w1j">
                      <ServiceDetail service={service} data-id="element-284" data-mp="6okeav0cs39n" />
                    </motion.li>}
                </AnimatePresence>
              </React.Fragment>;
        })}
        </ul>

        <div className="mt-10 flex flex-col items-start gap-4 rounded-3xl border border-blush-200 bg-cream p-7 sm:flex-row sm:items-center sm:justify-between" data-id="element-285" data-mp="2dh1ly4qnb7hw">
          <p className="text-base text-ink-soft" data-id="element-286" data-mp="2n7mf3cnjvjtf">
            <span className="font-semibold text-ink" data-id="element-287" data-mp="2i9unkjqthzpr">¿No sabés qué te conviene?</span> Te asesoro
            antes de reservar según el estado de tus uñas.
          </p>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-fucsia-deep px-7 py-3.5 text-sm font-semibold text-white transition-[transform,background-color] duration-150 ease-smooth hover:-translate-y-0.5 hover:bg-fucsia-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fucsia-deep" data-id="element-288" data-mp="1ukiinw2iwdt9">
            <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-289" data-mp="3axyvlbwv2ovm" />
            Consultar por WhatsApp
          </a>
        </div>
      </div>
    </section>;
}